/**
 * TDBRecipesEditor — view / add / edit / delete TDB recipes.
 *
 * Backed by GET / POST / DELETE /api/tdb/recipes. Sits in the
 * "Recipe Editor" tab inside /tdb/cost OR rendered inline on the
 * TDB division page (caller decides).
 *
 * Two recipe kinds:
 *   • flavor  — (base, protein) keyed · applies to every Shopify
 *               line-item carrying those flavor tokens (e.g. every
 *               "Pawsome 2.0 - Oats / Chicken / 500g" SKU resolves
 *               to the same flavor recipe).
 *   • product — recipe_key keyed by lowercased name · applies to
 *               specific SKUs (Spreads / Jellos / Fro Yo / Dogaroon).
 *
 * Every change is logged with `updated_by` + `updated_at` on the
 * recipe doc so audit trail is preserved.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, Edit3, BookOpen } from 'lucide-react';

const BASES = ['oats', 'ragi'];
const PROTEIN_SUGGESTIONS = [
  'chicken', 'chicken_liver', 'mutton', 'banana', 'cheeku',
  'mango', 'coconut', 'veggies', 'peanut_butter', 'blueberry',
  'pumpkin', 'cheese', 'apple_honey',
];

const fINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const STATUS_TONE = (pct) => {
  if (pct == null) return { fg: '#6B7280', bg: '#F7F5F2' };
  if (pct <= 15) return { fg: '#0F766E', bg: '#E8F7F4' };
  if (pct <= 25) return { fg: '#92400E', bg: '#FEF6E4' };
  return { fg: '#A32D2D', bg: '#FCEBEB' };
};

export default function TDBRecipesEditor() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null); // { kind, recipe_key } | { __new: true }
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/tdb/recipes');
      setRecipes(r.data.recipes || []);
    } catch (e) {
      if (e?.response?.status === 403) setRecipes([]);
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = (kind) => {
    setEditing({ __new: true });
    setForm({
      kind,
      base: kind === 'flavor' ? 'oats' : '',
      protein: kind === 'flavor' ? 'chicken' : '',
      recipe_key: '',
      source_name: '',
      total_cost: '',
      selling_price: '',
    });
  };

  const startEdit = (rec) => {
    setEditing(rec);
    setForm({ ...rec });
  };

  const cancel = () => {
    setEditing(null);
    setForm(null);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const body = {
        kind: form.kind,
        total_cost: Number(form.total_cost),
        selling_price: Number(form.selling_price),
        source_name: form.source_name || '',
        source_sheet: form.source_sheet || 'manual',
      };
      if (form.kind === 'flavor') {
        body.base = form.base;
        body.protein = form.protein;
      } else {
        body.recipe_key = form.recipe_key;
      }
      const r = await api.post('/tdb/recipes', body);
      toast.success(`Recipe saved · ${r.data.food_cost_pct}% food cost`);
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete recipe "${rec.recipe_key}"?`)) return;
    try {
      await api.delete(`/tdb/recipes?kind=${rec.kind}&recipe_key=${encodeURIComponent(rec.recipe_key)}`);
      toast.success('Recipe deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const filtered = recipes.filter((r) => {
    const q = filter.toLowerCase().trim();
    if (!q) return true;
    return (
      (r.recipe_key || '').toLowerCase().includes(q) ||
      (r.source_name || '').toLowerCase().includes(q) ||
      (r.source_sheet || '').toLowerCase().includes(q) ||
      (r.base || '').toLowerCase().includes(q) ||
      (r.protein || '').toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="text-xs text-[#9CA3AF] p-3">Loading recipes…</div>;
  if (error) return (
    <div className="rounded-md px-3 py-2 text-xs"
      style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}>
      Recipes error: {error}
    </div>
  );

  return (
    <div className="rounded-[10px] bg-white border p-4" style={{ borderColor: '#E5E1DC' }}
      data-testid="tdb-recipes-editor">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BookOpen size={16} style={{ color: '#185FA5' }} />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">TDB · Recipe Editor</h3>
          <span className="text-[10px] text-[#9CA3AF]">{recipes.length} recipes · edit anything below to update the Boston matrix margins live</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by name / flavor / sheet…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1 text-xs rounded border outline-none focus:border-[#2A7B88]"
            style={{ borderColor: '#E5E1DC', minWidth: 200 }}
            data-testid="recipe-filter"
          />
          <button onClick={() => startNew('flavor')}
            className="text-[11px] px-2 py-1 rounded text-white flex items-center gap-1 hover:opacity-90"
            style={{ backgroundColor: '#0F766E' }}
            data-testid="recipe-add-flavor">
            <Plus size={11} /> Flavor
          </button>
          <button onClick={() => startNew('product')}
            className="text-[11px] px-2 py-1 rounded text-white flex items-center gap-1 hover:opacity-90"
            style={{ backgroundColor: '#185FA5' }}
            data-testid="recipe-add-product">
            <Plus size={11} /> Product
          </button>
        </div>
      </div>

      {/* Editor form */}
      {form && (
        <div className="rounded-md p-3 mb-3 border-2"
          style={{ borderColor: '#185FA5', backgroundColor: '#EEF4FA' }}
          data-testid="recipe-form">
          <p className="text-xs font-semibold text-[#185FA5] mb-2">
            {editing?.__new ? 'New' : 'Edit'} · {form.kind} recipe
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            {form.kind === 'flavor' ? (
              <>
                <Labelled label="Base">
                  <select value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })}
                    className="px-2 py-1 rounded border w-full" style={{ borderColor: '#E5E1DC' }}
                    data-testid="recipe-input-base">
                    {BASES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Labelled>
                <Labelled label="Protein / Flavor">
                  <input list="protein-suggestions" value={form.protein}
                    onChange={(e) => setForm({ ...form, protein: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    className="px-2 py-1 rounded border w-full" style={{ borderColor: '#E5E1DC' }}
                    data-testid="recipe-input-protein" />
                  <datalist id="protein-suggestions">
                    {PROTEIN_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
                  </datalist>
                </Labelled>
              </>
            ) : (
              <Labelled label="Recipe key (lowercase exact match against line-item name)">
                <input value={form.recipe_key}
                  onChange={(e) => setForm({ ...form, recipe_key: e.target.value.toLowerCase() })}
                  placeholder="e.g. peanut butter cookie"
                  className="px-2 py-1 rounded border w-full" style={{ borderColor: '#E5E1DC' }}
                  data-testid="recipe-input-key" />
              </Labelled>
            )}
            <Labelled label="Total cost (₹)">
              <input type="number" value={form.total_cost}
                onChange={(e) => setForm({ ...form, total_cost: e.target.value })}
                className="px-2 py-1 rounded border w-full" style={{ borderColor: '#E5E1DC' }}
                data-testid="recipe-input-cost" />
            </Labelled>
            <Labelled label="Selling price (₹)">
              <input type="number" value={form.selling_price}
                onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
                className="px-2 py-1 rounded border w-full" style={{ borderColor: '#E5E1DC' }}
                data-testid="recipe-input-sp" />
            </Labelled>
            <Labelled label="Source name (optional · for audit trail)">
              <input value={form.source_name || ''}
                onChange={(e) => setForm({ ...form, source_name: e.target.value })}
                placeholder="e.g. Oats Blueberry"
                className="px-2 py-1 rounded border w-full md:col-span-2" style={{ borderColor: '#E5E1DC' }}
                data-testid="recipe-input-source-name" />
            </Labelled>
          </div>

          {/* Live food cost preview */}
          {form.total_cost > 0 && form.selling_price > 0 && (
            <p className="text-[11px] mt-2 text-[#185FA5]">
              → Food cost <strong>{(Number(form.total_cost) / Number(form.selling_price) * 100).toFixed(1)}%</strong>
              {' '}· margin <strong>{(100 - Number(form.total_cost) / Number(form.selling_price) * 100).toFixed(1)}%</strong>
              {' '}· contribution <strong>₹{(Number(form.selling_price) - Number(form.total_cost)).toFixed(0)}/u</strong>
            </p>
          )}

          <div className="flex items-center gap-2 mt-2">
            <button onClick={save} disabled={saving}
              className="text-[11px] px-3 py-1 rounded text-white flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#0F766E' }}
              data-testid="recipe-save">
              <Save size={11} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={cancel}
              className="text-[11px] px-3 py-1 rounded border flex items-center gap-1 hover:bg-white"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
              data-testid="recipe-cancel">
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="recipes-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6B7280] border-b" style={{ borderColor: '#E5E1DC' }}>
              <th className="py-1.5 pr-2">Kind</th>
              <th className="py-1.5 pr-2">Key</th>
              <th className="py-1.5 pr-2">Source</th>
              <th className="py-1.5 pr-2 text-right">Cost</th>
              <th className="py-1.5 pr-2 text-right">SP</th>
              <th className="py-1.5 pr-2 text-right">Food cost %</th>
              <th className="py-1.5 pr-2 text-right">Margin</th>
              <th className="py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((rec) => {
              const tone = STATUS_TONE(rec.food_cost_pct);
              const margin = rec.selling_price ? (rec.selling_price - rec.total_cost) : 0;
              return (
                <tr key={`${rec.kind}-${rec.recipe_key}`} className="border-b hover:bg-[#FAFAF7]"
                  style={{ borderColor: '#F0EBE5' }}
                  data-testid={`recipe-row-${rec.kind}-${rec.recipe_key.replace(/[^a-z0-9]/gi, '-')}`}>
                  <td className="py-1.5 pr-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold"
                      style={{ backgroundColor: rec.kind === 'flavor' ? '#FEF6E4' : '#EEF4FA', color: rec.kind === 'flavor' ? '#92400E' : '#185FA5' }}>
                      {rec.kind}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 font-medium text-[#1B2A4A]">{rec.recipe_key}</td>
                  <td className="py-1.5 pr-2 text-[#6B7280]">
                    {rec.source_name || '—'}
                    {rec.source_sheet && <span className="text-[#9CA3AF]"> · {rec.source_sheet}</span>}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{fINR(rec.total_cost)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{fINR(rec.selling_price)}</td>
                  <td className="py-1.5 pr-2 text-right">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      style={{ backgroundColor: tone.bg, color: tone.fg }}>
                      {rec.food_cost_pct}%
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-[#0F766E]">{fINR(margin)}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(rec)}
                      className="text-[#185FA5] hover:bg-[#EEF4FA] p-1 rounded mr-1"
                      title="Edit"
                      data-testid={`recipe-edit-${rec.kind}-${rec.recipe_key.replace(/[^a-z0-9]/gi, '-')}`}>
                      <Edit3 size={11} />
                    </button>
                    <button onClick={() => remove(rec)}
                      className="text-[#A32D2D] hover:bg-[#FCEBEB] p-1 rounded"
                      title="Delete"
                      data-testid={`recipe-delete-${rec.kind}-${rec.recipe_key.replace(/[^a-z0-9]/gi, '-')}`}>
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-3 text-center text-[11px] italic text-[#9CA3AF]">No recipes match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Labelled({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[9px] uppercase tracking-widest font-semibold text-[#6B7280] mb-0.5">{label}</span>
      {children}
    </label>
  );
}
